import React, {useMemo, useState} from 'react';

import {ModalComponentProps, Dialog, DialogBody, Card, DialogFooter, Button, Row, Col, VF, Block, Yup, makeValidate} from '@mc/ui-v4';

import {Form} from 'react-final-form';
import {LayerFileDropzone} from './LayerFileDropzone';
import {ExportImportApi, LayerDataResponseVO} from '../../../api';
import {ExternalType} from '@mc/react-mapio';
import {useMapioContext} from '../../../mapioContext';
import {formatExternalLayersToCatalogItemVO} from '../../../helpers/Formatter';

type Props = ModalComponentProps & {
    title: string;
    type: ExternalType;
};

// Dynamické schéma na základě typu
const getValidationSchema = type => {
    switch (type) {
        case ExternalType.WMS:
            return Yup.object({
                url: Yup.string()
                    .matches(
                        /^(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})$/,
                        'Neplatná URL',
                    )
                    .required('URL je povinná'),
            });
        case ExternalType.FILE:
            return Yup.object({
                files: Yup.array().of(Yup.mixed().required('Soubor je povinný')).required('Je vyžadován alespoň jeden soubor'),
            });
        default:
            return Yup.object({});
    }
};

export const AddExternalLayer: React.FC<Props> = ({onClose, title, type}) => {
    const mapioContext = useMapioContext();
    const {setActiveLayers} = mapioContext;
    const {mapRef, activeLayers} = mapioContext.value;

    const validationSchema = getValidationSchema(type);
    const [fileObjects, setFileObjects] = useState<LayerDataResponseVO[]>([]);

    const handleChangeFileObjects = (vals, change) => {
        setFileObjects(vals);
        change('files', vals);
    };

    const onSubmit = (values: any) => {
        mapRef.current.loadExternalLayers(type, {url: values.url, proxy: values.proxy, layers: fileObjects as any});

        mapioContext.set(prevState => {
            // Vypočítáme allLayerIds přímo zde
            const updatedExternalLayers = [...prevState.externalLayers, ...(fileObjects as any)];
            const formattedExternalLayers = formatExternalLayersToCatalogItemVO(updatedExternalLayers);

            // Vrátíme aktualizovaný stav
            return {
                ...prevState,
                externalLayers: updatedExternalLayers,
                externalLayersCatalog: formattedExternalLayers,
            };
        });
        // Není třeba znovu počítat allLayerIds zde, protože už je v kontextu
        setActiveLayers([...activeLayers, ...fileObjects.map(layer => layer.id)]);
        onClose();
    };
    const validate = useMemo(() => makeValidate(validationSchema), []);

    return (
        <Form onSubmit={onSubmit} validate={validate}>
            {({submitting, handleSubmit, form: {change}}) => (
                <Dialog size={'md'} onClose={onClose} title={title}>
                    <DialogBody>
                        <Card mode={'edit'}>
                            <Row>
                                {type === ExternalType.WMS && (
                                    <Col className={'px-2'}>
                                        <VF.String
                                            name="url"
                                            label={'Url'}
                                            required
                                            disabled={submitting}
                                            //  onChange={e => setUrl(e.target.value)}
                                        />
                                        <VF.Checkbox name="proxy" label={'Použít proxy'} type={'checkbox'} disabled={submitting} />
                                    </Col>
                                )}
                                {type === ExternalType.FILE && (
                                    <Block className="mx-auto" bodyClassName="pb-0">
                                        <VF.Base
                                            name="files"
                                            input={LayerFileDropzone}
                                            label={false}
                                            onDocumentUpload={async (imageContent: File) => {
                                                const {data} = await ExportImportApi.importFeatures([imageContent]);
                                                return data;
                                            }}
                                            fileObjects={fileObjects}
                                            setFileObjects={vals => handleChangeFileObjects(vals, change)}
                                            multiple
                                        />
                                    </Block>
                                )}
                            </Row>
                        </Card>
                    </DialogBody>
                    <DialogFooter>
                        <Button onClick={onClose} style={'link'}>
                            Zrušit
                        </Button>
                        <Button disabled={submitting} style="primary" className="add-external" onClick={handleSubmit} type={'submit'}>
                            Připojit
                        </Button>
                    </DialogFooter>
                </Dialog>
            )}
        </Form>
    );
};
